import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UserManagementService } from "../user-management.service";
import Swal from "sweetalert2";
import { SignupconfirmComponent } from "../signup-confirm/signup-confirm.component";
import { ModalService } from "src/app/service/modal.service";
import { debounceTime, distinctUntilChanged } from "rxjs";

/**
 * One page of the phone wizard.
 *
 * `optional` drives the Skip control. Every field on this form is currently
 * declared with Validators.required, so no step qualifies and no Skip button
 * renders. Relaxing a validator and flipping this flag is all that is needed
 * to make a step skippable.
 */
interface SignupStep {
  title: string;
  fields: string[];
  optional: boolean;
}

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.css"],
  standalone: false,
})
export class SignupComponent implements OnInit, OnDestroy {
  signupForm: FormGroup;
  hidePassword = true;
  hideConfirmPassword = true;
  username: string = "";
  availabilityMessage: string = "";
  isAvailable: boolean | null = null; // true: available, false: unavailable, null: no check yet

  /**
   * Phone-only. Desktop shows every field at once and never reads currentStep;
   * the steps below are the same controls in the same FormGroup, so validation
   * and submission are shared by both layouts.
   */
  readonly steps: SignupStep[] = [
    { title: "Account", fields: ["username"], optional: false },
    { title: "Your name", fields: ["firstName", "lastName"], optional: false },
    { title: "Contact", fields: ["email", "phone_number"], optional: false },
    {
      title: "Security",
      fields: ["pass", "confirmPassword", "acceptTerms"],
      optional: false,
    },
  ];

  currentStep = 0;
  isStepped = false;

  private stepQuery?: MediaQueryList;

  private readonly applyStepMode = (
    event: MediaQueryListEvent | MediaQueryList,
  ): void => {
    this.isStepped = event.matches;
    // Rotating to a wide screen reveals the whole form; leaving currentStep
    // parked would strand the user mid-wizard if they rotated back.
    if (!this.isStepped) {
      this.currentStep = 0;
    }
  };

  constructor(
    private fb: FormBuilder,
    private userService: UserManagementService,
    private activeModal: NgbActiveModal,
    private mservice: ModalService,
  ) {}

  ngOnInit() {
    this.signupForm = this.fb.group(
      {
        username: ["", [Validators.required, Validators.minLength(3), Validators.pattern(/^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u)]],
        firstName: ["", Validators.required],
        lastName: ["", Validators.required],
        email: [
          "",
          [
            Validators.required,
            Validators.pattern(
              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            ),
          ],
        ], // Improved regex
        phone_number: [
          "",
          [Validators.required, Validators.pattern(/^\d{10}$/)],
        ],
        pass: [
          "",
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),
          ],
        ],
        confirmPassword: ["", Validators.required],
        acceptTerms: [false, Validators.requiredTrue],
      },
      { validator: this.passwordMatchValidator },
    );

    // Same 767.98px boundary the stylesheet uses. CSS decides which fields are
    // visible; this decides whether Continue/Back exist at all and whether the
    // keyboard's Go key advances or submits.
    if (typeof window !== "undefined" && window.matchMedia) {
      this.stepQuery = window.matchMedia("(max-width: 767.98px)");
      this.applyStepMode(this.stepQuery);
      this.stepQuery.addEventListener("change", this.applyStepMode);
    }
  }

  ngOnDestroy(): void {
    this.stepQuery?.removeEventListener("change", this.applyStepMode);
  }

  get activeStep(): SignupStep {
    return this.steps[this.currentStep];
  }

  get isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  get canAdvance(): boolean {
    return this.isStepComplete(this.currentStep);
  }

  isStepComplete(index: number): boolean {
    const step = this.steps[index];
    if (!step || !this.signupForm) {
      return false;
    }
    if (!step.fields.every((f) => this.signupForm.get(f)?.valid)) {
      return false;
    }
    // mismatch lives on the group, not on either password control
    if (
      step.fields.includes("confirmPassword") &&
      this.signupForm.hasError("mismatch")
    ) {
      return false;
    }
    if (step.fields.includes("username") && this.isAvailable === false) {
      return false;
    }
    return true;
  }

  next(): void {
    if (!this.isStepComplete(this.currentStep)) {
      this.touchStep(this.currentStep);
      return;
    }
    if (!this.isLastStep) {
      this.currentStep++;
    }
  }

  back(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  /** Clears an optional step's controls and moves on. */
  skip(): void {
    if (!this.activeStep.optional || this.isLastStep) {
      return;
    }
    this.activeStep.fields.forEach((f) => {
      const control = this.signupForm.get(f);
      control?.reset(f === "acceptTerms" ? false : "");
      control?.markAsUntouched();
    });
    this.currentStep++;
  }

  private touchStep(index: number): void {
    this.steps[index]?.fields.forEach((f) =>
      this.signupForm.get(f)?.markAsTouched(),
    );
  }

  getErrorMessage(controlName: string): string {
    const control = this.signupForm.get(controlName);
    if (control?.hasError("required")) {
      return `${controlName.charAt(0).toUpperCase() + controlName.slice(1)} is required`;
    }
    if (control?.hasError("email")) {
      return "Invalid email address";
    }
    if (control?.hasError("minlength")) {
      return `Minimum length is ${control.errors?.["minlength"].requiredLength} characters`;
    }
    if (control?.hasError("pattern")) {
      if (controlName === "username") {
        return "Username can only contain letters, marks, symbols, numbers, and punctuation.";
      }
      if (controlName === "pass") {
        return "Password must include uppercase, lowercase, and a number.";
      }
      if (controlName === "phone_number") {
        return "Enter a valid 10 digit mobile number.";
      }
      return "Invalid format";
    }
    if (
      controlName === "confirmPassword" &&
      this.signupForm.hasError("mismatch")
    ) {
      return "Passwords do not match";
    }
    return "";
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get("pass")?.value === g.get("confirmPassword")?.value
      ? null
      : { mismatch: true };
  }

  onSubmit() {
    // In wizard mode the form is only half-filled until the final step, so a
    // stray Enter or the keyboard's Go key must advance rather than submit.
    if (this.isStepped && !this.isLastStep) {
      this.next();
      return;
    }

    if (this.signupForm.valid) {
      const localPhone = this.normalizePhone(
        this.signupForm.value.phone_number,
      );
      if (localPhone.length !== 10) {
        this.signupForm.get("phone_number")?.setErrors({ pattern: true });
        this.signupForm.get("phone_number")?.markAsTouched();
        return;
      }

      let request = {
        username: this.signupForm.value.username,
        first_name: this.signupForm.value.firstName,
        last_name: this.signupForm.value.lastName,
        password: this.signupForm.value.pass,
        email: this.signupForm.value.email,
        phone: `+91${localPhone}`,
      };

      this.userService
        .signup(request)
        .then((response: any) => {
          if (response?.success) {
            this.activeModal.dismiss();
            this.openSignupConfirmModal(this.signupForm.value.username);
          } else {
            this.showAlert(
              response?.message || "Signup failed. Please try again.",
            );
          }
        })
        .catch((error: any) => {
          this.showAlert(
            error?.error?.message ||
              error?.message ||
              "Signup failed. Please try again.",
          );
        });
    } else {
      Object.keys(this.signupForm.controls).forEach((key) => {
        const control = this.signupForm.get(key);
        if (control?.invalid) {
          control.markAsTouched();
        }
      });
    }
  }
  showAlert(msg) {
    Swal.fire({
      title: msg,
      showClass: {
        popup: "animate__animated animate__fadeInDown",
      },
      hideClass: {
        popup: "animate__animated animate__fadeOutUp",
      },
    });
  }
  private openSignupConfirmModal(username) {
    const modalRef = this.mservice.open(SignupconfirmComponent, {
      options: {
        backdrop: false,
        keyboard: true,
      },
    });
    modalRef.componentInstance.username = username;
  }
  public dissmissModal() {
    this.activeModal.dismiss();
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = this.normalizePhone(input.value).slice(0, 10);
    if (input.value !== digitsOnly) {
      input.value = digitsOnly;
    }
    this.signupForm
      .get("phone_number")
      ?.setValue(digitsOnly, { emitEvent: false });
  }

  private normalizePhone(value: string): string {
    return (value || "").replace(/\D/g, "");
  }

  checkUsernameAvailability(username: string): void {
    if (username.trim().length < 3) {
      this.availabilityMessage = "Username must be at least 3 characters long.";
      this.isAvailable = null;
      return;
    }
    let request = {
      username: this.signupForm.value.username,
    };
    this.userService
      .isUsernameAvailable(request)
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe({
        next: (response: any) => {
          const available = response?.data?.available === true;
          this.isAvailable = available;
          this.availabilityMessage =
            response?.message ||
            (available
              ? "Username is available!"
              : "Username is already taken.");
        },
        error: (error: any) => {
          this.isAvailable = null;
          this.availabilityMessage =
            error?.error?.message || "Unable to verify username right now.";
        },
      });
  }
}
